import {useEffect, useState} from "react";

import api from "./api.js";
import Auth from "./Auth.js";
import {Auth as LibAuth} from "@digatex/digatex-ui-lib";
import {createOperators, normalizeWhitespace, getDefaultOperators, getSearchAttempt} from "./helpers.js";

import {Menu} from "./layouts/Menu/Menu.jsx";

import styles from "./App.module.css";

import FileDownload from "js-file-download";

import {Info} from "@/components/Info/Info.jsx";
import {SearchBar} from "@/components/SearchBar/SearchBar.jsx";
import {SearchComponent} from "@/components/SearchComponent/SearchComponent.jsx";
import {App as AntdApp} from "antd";
import {ResultsTop} from "@/components/ResultsTop/ResultsTop.jsx";
import {useLocation, useNavigate} from "react-router-dom";
import {ItemTable} from "@/components/ItemTable/ItemTable.jsx";
import {PageTitle} from "./components/custom/PageTitle.jsx";
import {
	clearSearchCache,
	hasSearchCache,
	loadSearchCache,
	saveSearchCache,
} from "@/utils/searchSessionCache/searchSessionCache.js";

const SEARCH_DEFAULTS_PARAMS = {
	top: 50,
	showAll: true,
	equipment: true,
	operator: [],
	query: "",
};

export const App = () => {
	const [data, setData] = useState(() => ({
		results: [],
		operators: [],
		lastApplied: structuredClone(SEARCH_DEFAULTS_PARAMS),
	}));

	const [params, setParams] = useState(() => structuredClone(SEARCH_DEFAULTS_PARAMS));

	const [ui, setUi] = useState({
		loading: false,
		shouldSet: null,
		showEdit: false,
	});

	const userRoles = LibAuth.tokenParsed()?.resource_access?.["inventory-api"]?.roles ?? [];
	const showCommonality = !userRoles.includes("internal");

	const [info, setInfo] = useState(null);

	const {message, modal} = AntdApp.useApp();

	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		const requests = [
			api.get(`/info`, { omitGlobalError: true }),
			api.get(`/operators?global=true`, { omitGlobalError: true }),
			api.get(`/settings/table-overridden?name=Default Operators`, { omitGlobalError: true }),
		];

		Promise.allSettled(requests)
			.then(([infoResponse, operatorsResponse, settingsResponse]) => {
				if (infoResponse.status === "fulfilled") {
					setInfo(infoResponse.value.data);
				}

				if (infoResponse.status === "rejected") {
					console.log(infoResponse.reason?.response?.data);
				}

				if (operatorsResponse.status === "fulfilled") {
					const operatorsValueData = operatorsResponse.value.data;
					let defaultOps = [];

					if (settingsResponse.status === "fulfilled" && settingsResponse.value.data?.length > 0) {
						defaultOps = settingsResponse.value.data;
					} else {
						defaultOps = getDefaultOperators(operatorsValueData);
					}

					setData(prev => ({
						...prev,
						operators: operatorsValueData,
					}));

					if (!location.state?.params && !hasSearchCache(location.key)) {
						setParams(prev => ({
							...prev,
							operator: defaultOps,
						}));
					}
				}

				if (operatorsResponse.status === "rejected") {
					console.log(operatorsResponse.reason);
				}

				if (settingsResponse.status === "rejected") {
					console.log(settingsResponse.reason);
				}
			})
			.catch(() => {
				message.error("System initialization error");
			});
	}, []);

	useEffect(() => {
		if (location.state?.reset) {
			clearSearchCache();

			const defaultOps = getDefaultOperators(data.operators ?? []);

			setData(prev => ({
				...prev,
				results: [],
				lastApplied: {
					...SEARCH_DEFAULTS_PARAMS,
					operator: [...defaultOps],
				},
			}));

			setParams({
				...SEARCH_DEFAULTS_PARAMS,
				operator: [...defaultOps],
			});

			navigate(location.pathname, {replace: true, state: {}});
			return;
		}

		if (location.state?.params) {
			const {results, params: savedParams} = location.state;
			setData(prev => ({
				...prev,
				results: results ?? [],
				lastApplied: savedParams,
			}));
			setParams(savedParams);
			return;
		}

		const cached = loadSearchCache();
		if (cached?.params) {
			setData(prev => ({
				...prev,
				results: cached.results ?? [],
				lastApplied: cached.params,
			}));
			setParams(cached.params);
		}
	}, [location.key, location.state]);

	function warnIfNoOperators(operator) {
		if (operator?.length) return false;

		modal.warning({
			title: "Select Operator",
			content: "At least one operator must be selected.",
		});
		return true;
	}

	function executeSearch(string, showAll, equipment, operator, fullOperators, resend, top) {
		const {cleanString, shouldSearch} = getSearchAttempt({
			query: string,
			showAll,
			equipment,
			operator,
			top,
			lastApplied: data.lastApplied,
			resend,
		});

		if (!shouldSearch) return;

		if (warnIfNoOperators(operator)) return;

		setData(prev => ({
			...prev,
			results: [],
		}));

		setUi(prev => ({
			...prev,
			loading: true,
		}));

		api.post(
			`/search?top=${top}&query=${encodeURIComponent(cleanString)}&showAll=${showAll}&equipment=${equipment}`,
			createOperators(fullOperators, operator),
		)
			.then(json => {
				const currentParams = {query: cleanString, showAll, equipment, operator, top};

				const searchResultsData = json.data.results;

				setData(prev => ({
					...prev,
					results: searchResultsData,
					lastApplied: currentParams,
				}));

				saveSearchCache({results: searchResultsData, params: currentParams});

				navigate(location.pathname, {state: {results: searchResultsData, params: currentParams}});
			})
			.catch(error => {
				console.log(error.response?.data);
			})
			.finally(() => {
				setUi(prev => ({
					...prev,
					loading: false,
				}));
			});
	}

	function handleDownLoad() {
		const items = data.results;

		const config = Auth.headers();
		config["responseType"] = "blob";

		api.post(`/search/download?search=${data.lastApplied?.query}`, items, config)
			.then(response => {
				const header = response.headers["content-disposition"];
				const filename = /filename=(.*)/.exec(header)[1];
				FileDownload(response.data, filename, filename);
			})
			.catch(error => alert(error));
	}

	function handleTopChange(newTop) {
		const {shouldSearch} = getSearchAttempt({
			query: params.query,
			showAll: params.showAll,
			equipment: params.equipment,
			operator: params.operator,
			top: newTop,
			lastApplied: data.lastApplied,
		});

		if (shouldSearch && warnIfNoOperators(params.operator)) return;

		setParams(prev => ({...prev, top: newTop}));
		executeSearch(params.query, params.showAll, params.equipment, params.operator, data.operators, false, newTop);
	}

	function handleSearch(event) {
		event && event.preventDefault();

		if (ui.loading) {
			message.error("Previous query is still executing. Please wait till the results show up", 5);
			return;
		}

		executeSearch(
			params.query,
			params.showAll,
			params.equipment,
			params.operator,
			data.operators,
			false,
			params.top,
		);
	}

	function handleLoading(startLoading, shouldSet) {
		setUi(prev => ({
			...prev,
			loading: startLoading,
			shouldSet: shouldSet !== undefined ? shouldSet : prev.shouldSet,
		}));
	}

	function onAttrClick(attr) {
		setParams(prev => {
			let fullString = prev.query;

			if (fullString.includes(attr.text)) {
				fullString = fullString.replace(attr.text, "");
			} else {
				fullString = fullString + " " + attr.text;
			}

			return {
				...prev,
				query: normalizeWhitespace(fullString),
			};
		});
	}

	return (
		<div className={styles.appWrapper}>
			<PageTitle />
			<Menu
				search={
					<SearchComponent
						searchInProgress={ui.loading}
						query={params.query}
						onQueryChange={value => setParams(prev => ({...prev, query: value}))}
						sendRequest={handleSearch}
					/>
				}
			/>
			<div className={styles.controlsPanel}>
				<SearchBar
					operators={data.operators}
					operator={params.operator}
					showAll={params.showAll}
					equipment={params.equipment}
					onShowAllChange={value => setParams(prev => ({...prev, showAll: value}))}
					onEquipmentChange={value => setParams(prev => ({...prev, equipment: value}))}
					onOperatorChange={value => setParams(prev => ({...prev, operator: value}))}
				/>
				<Info info={info} />
			</div>
			<div className={styles.mainViewport}>
				<div className={styles.appRoot}>
					<ItemTable
						showCommonality={showCommonality}
						showAll={params.showAll}
						handleLoading={handleLoading}
						shouldSet={ui.shouldSet}
						handleDownload={handleDownLoad}
						search={data.lastApplied.query}
						values={data.results}
						onRequestAttr={onAttrClick}
					/>
					<ResultsTop loading={ui.loading} currentTop={params.top} onTopChange={handleTopChange} />
				</div>
			</div>
		</div>
	);
};
